'use client';

import { useRef, useState, type ChangeEvent } from 'react';
import { HStack } from '@terra/ui-styled-system';
import {
  ConfirmationDialog,
  IconButton,
  Tooltip,
} from '@terra/ui-design-system';
import {
  Download01Icon,
  RotateCcwSquareIcon,
  Upload01Icon,
} from '@terra/ui-icons';
import { SettingsPage } from '../SettingsPage';
import { useProxyStore } from '../../store/proxy-store';
import { toasterApi } from '../../app/providers';
import { useTranslations } from 'next-intl';

export function ProxySettingsTabWrapper({ tabId }: { tabId: string }) {
  const tProxySettings = useTranslations('settings.proxySettingsActions');
  const tCommon = useTranslations('common');
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const { exportSettings, importSettings, resetSettings } = useProxyStore();
  const importInputRef = useRef<HTMLInputElement>(null);

  const handleExportSettings = () => {
    try {
      const settingsJson = exportSettings();
      const blob = new Blob([settingsJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = 'proxy-settings.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toasterApi.success({
        title: tProxySettings('exportSuccessTitle'),
        description: tProxySettings('exportSuccessDescription'),
      });
    } catch (error: unknown) {
      console.error('Failed to export proxy settings:', error);
      toasterApi.error({
        title: tProxySettings('exportFailedTitle'),
        description: tProxySettings('exportFailedDescription'),
      });
    }
  };

  const handleImportSettings = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result;

        importSettings(typeof content === 'string' ? content : '');
        toasterApi.success({
          title: tProxySettings('importSuccessTitle'),
          description: tProxySettings('importSuccessDescription'),
        });
      } catch (error: unknown) {
        console.error('Failed to import proxy settings:', error);
        toasterApi.error({
          title: tProxySettings('importFailedTitle'),
          description: tProxySettings('importFailedDescription'),
        });
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const handleResetSettings = () => {
    resetSettings();
    setIsResetConfirmOpen(false);
    toasterApi.success({
      title: tProxySettings('resetSuccessTitle'),
      description: tProxySettings('resetSuccessDescription'),
    });
  };

  const headerAction = (
    <HStack gap="1">
      <input
        ref={importInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleImportSettings}
      />
      <Tooltip title={tProxySettings('importSettings')}>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={() => importInputRef.current?.click()}
          aria-label={tProxySettings('importSettings')}
        >
          <Upload01Icon size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title={tProxySettings('exportSettings')}>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleExportSettings}
          aria-label={tProxySettings('exportSettings')}
        >
          <Download01Icon size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title={tProxySettings('resetSettings')}>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={() => setIsResetConfirmOpen(true)}
          aria-label={tProxySettings('resetSettings')}
        >
          <RotateCcwSquareIcon size={16} />
        </IconButton>
      </Tooltip>
      <ConfirmationDialog
        isOpen={isResetConfirmOpen}
        onOpenChange={({ open }) => setIsResetConfirmOpen(open)}
        onConfirm={handleResetSettings}
        variant="destructive"
        title={tProxySettings('resetConfirmTitle')}
        description={tProxySettings('resetConfirmDescription')}
        confirmText={tProxySettings('resetSettings')}
        cancelText={tCommon('cancel')}
      />
    </HStack>
  );

  return <SettingsPage tabId={tabId} headerAction={headerAction} />;
}
